import * as React from "react";
import { usePrivy, useFundWallet } from "@privy-io/react-auth";

export interface PrivyFundWalletProps {
  dotNetRef: any;
}

/**
 * Headless component for wallet funding with command-based execution.
 * Commands are sent from .NET via executeCommand exposed to window.fundWalletManager
 */
const PrivyFundWallet: React.FC<PrivyFundWalletProps> = (props) => {
  const { ready, authenticated, user } = usePrivy();
  const { fundWallet } = useFundWallet();
  const { dotNetRef } = props;

  const handleFundWallet = React.useCallback(async (options?: {
    address?: string;
    asset?: string;
    chain?: any;
    amount?: string;
  }) => {
    if (!authenticated) {
      dotNetRef.invokeMethodAsync(
        "OnEventCallback",
        JSON.stringify({
          type: "error",
          message: "You must be logged in to fund your wallet"
        })
      );
      return;
    }

    // Check if user has an embedded wallet
    // Sort by first_verified_at to get the primary wallet (first_verified_at: 0)
    const embeddedWallets = user?.linkedAccounts
      .filter(
        (account: any) =>
          account.type === 'wallet' &&
          account.walletClientType === 'privy' &&
          account.chainType === 'ethereum'
      )
      .sort((a: any, b: any) => (a.first_verified_at || 0) - (b.first_verified_at || 0));

    const embeddedWallet = embeddedWallets?.[0] as any;

    if (!embeddedWallet) {
      dotNetRef.invokeMethodAsync(
        "OnEventCallback",
        JSON.stringify({
          type: "error",
          message: "No embedded wallet found"
        })
      );
      return;
    }

    try {
      // Open the fund wallet modal
      await fundWallet({
        address: options?.address || embeddedWallet.address,
        ...(options?.asset && { asset: options.asset }),
        ...(options?.chain && { chain: options.chain }),
        ...(options?.amount && { amount: options.amount })
      });

      // Notify .NET that funding was initiated
      dotNetRef.invokeMethodAsync(
        "OnEventCallback",
        JSON.stringify({
          type: "fund_wallet_opened",
          address: options?.address || embeddedWallet.address
        })
      );
    } catch (error: any) {
      dotNetRef.invokeMethodAsync(
        "OnEventCallback",
        JSON.stringify({
          type: "error",
          message: error.message || "Failed to open fund wallet modal"
        })
      );
    }
  }, [authenticated, user, fundWallet, dotNetRef]);

  const checkWalletStatus = React.useCallback(() => {
    const embeddedWallet = user?.linkedAccounts.find(
      (account: any) =>
        account.type === 'wallet' &&
        account.walletClientType === 'privy' &&
        account.chainType === 'ethereum'
    ) as any;

    dotNetRef.invokeMethodAsync(
      "OnEventCallback",
      JSON.stringify({
        type: "wallet_status",
        authenticated: authenticated,
        hasEmbeddedWallet: !!embeddedWallet,
        walletAddress: (embeddedWallet?.address as string) || null
      })
    );
  }, [authenticated, user, dotNetRef]);

  // Expose commands to window for .NET to call
  React.useEffect(() => {
    (window as any).fundWalletManager = {
      fundWallet: handleFundWallet,
      checkWalletStatus
    };

    // Notify .NET that React is ready
    if (dotNetRef && ready) {
      dotNetRef.invokeMethodAsync("OnEventCallback", JSON.stringify({
        type: "ready"
      }));
    }

    // Auto-check status on mount
    if (ready) {
      checkWalletStatus();
    }

    // Cleanup on unmount
    return () => {
      delete (window as any).fundWalletManager;
    };
  }, [handleFundWallet, checkWalletStatus, dotNetRef, ready]);

  // Headless component - renders no UI
  return null;
};

export default PrivyFundWallet;

